import { useEffect, useRef, useState } from 'react';
import { useToast } from '../lib/toast-context';
import { FAVORITES_CHANGED, type FavoritesChangedDetail } from '../lib/events';
import { toggleAssetFavorite } from '../lib/favorite-toggle';

/**
 * SSOT für den ⭐-Favoriten-Toggle (Library-Grid + -Liste + Quick-Look-Overlay).
 * Optimistic local state + Single-Flight (zweiter Klick während eines laufenden
 * Requests wird ignoriert → kein favorite→unfavorite-Race / out-of-order-Revert),
 * Revert + Fehler-Toast bei Reject.
 *
 * **Cross-Instanz-Sync (Roadmap 3a Phase 3):** lauscht zusätzlich auf
 * `FAVORITES_CHANGED` für die eigene `assetId` und folgt dem Stand — so bleiben
 * ALLE gemounteten Stern-Instanzen derselben id konsistent (z.B. Grid-Stern hinter
 * der Overlay aktualisiert sich, wenn man dort favorisiert). Idempotent
 * (`setFavorited(value)`, kein erneuter `toggle()` → kein Echo-Loop). Während eines
 * EIGENEN laufenden Toggles (`pendingRef`) wird das Event ignoriert, damit ein spät
 * eintreffendes Fremd-Event keinen laufenden Revert überschreibt.
 *
 * **`toast` ist ein Hook, kein Modul-Import** — dieser Hook ruft `useToast()`
 * intern; ein Test braucht entsprechend einen `<ToastProvider>`-Wrapper.
 *
 * Single-Flight läuft über einen Ref (synchron), nicht über den `pending`-State:
 * zwei schnelle Klicks lesen sonst beide das noch-`false` State und feuern doppelt.
 */
export function useFavoriteToggle(
  assetId: string,
  initialFavorited: boolean | undefined,
): { favorited: boolean; toggle: () => void; pending: boolean } {
  const [favorited, setFavorited] = useState(!!initialFavorited);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const toast = useToast();

  // Re-sync bei id-Wechsel oder server-Update (z.B. Refetch / Infinite-Scroll-
  // Append, bei dem dieselbe Card mit aktualisiertem Flag re-rendert).
  useEffect(() => {
    setFavorited(!!initialFavorited);
  }, [assetId, initialFavorited]);

  // Cross-Instanz-Sync: Favoriten-Change anderswo (anderer Stern / F-Shortcut /
  // Inspector) für DIESELBE id → mitziehen. `pendingRef`-Guard: eigene in-flight-
  // Toggles ignorieren das Echo (sonst überschreibt ein spätes Fremd-Event den Revert).
  useEffect(() => {
    function onChanged(e: Event): void {
      if (pendingRef.current) return;
      const detail = (e as CustomEvent<FavoritesChangedDetail>).detail;
      if (!detail || detail.assetId !== assetId) return;
      setFavorited(detail.favorited);
    }
    window.addEventListener(FAVORITES_CHANGED, onChanged);
    return () => window.removeEventListener(FAVORITES_CHANGED, onChanged);
  }, [assetId]);

  function toggle(): void {
    if (pendingRef.current) return; // Single-Flight
    const prev = favorited;
    const next = !prev;
    setFavorited(next); // optimistic
    pendingRef.current = true;
    setPending(true);
    void (async () => {
      try {
        // toggleAssetFavorite macht API + FAVORITES_CHANGED-Dispatch (für die
        // übrigen Instanzen; das eigene Echo ist via pendingRef-Guard inert).
        await toggleAssetFavorite(assetId, prev);
      } catch {
        setFavorited(prev); // Revert auf Vor-Klick-Stand
        toast.error(next ? 'Favorisieren fehlgeschlagen.' : 'Aus Favoriten entfernen fehlgeschlagen.');
      } finally {
        pendingRef.current = false;
        setPending(false);
      }
    })();
  }

  return { favorited, toggle, pending };
}
